import { View, Text } from 'react-native';
import {
  Award,
  Linkedin,
  Shield,
  Star,
  TrendingUp,
  CheckCircle,
  Zap,
  Clock,
  Users,
  Target,
  Trophy,
  Heart,
  Briefcase,
} from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { InvestorBadgeType } from '@/lib/state/inverio-store';

export type BadgeType =
  | 'serial-founder'
  | 'linkedin-verified'
  | 'investor-seniority'
  | 'actively-raising'
  | 'verified-investment'
  // New investor badge types
  | 'serial-investor'
  | 'lead-investor'
  | 'quick-responder'
  | 'first-check'
  | 'follow-on-champion'
  | 'founder-favorite'
  | 'board-expert'
  | 'sector-specialist';

export type SeniorityLevel = 'emerging' | 'experienced' | 'veteran';

interface BadgeProps {
  type: BadgeType | InvestorBadgeType;
  seniorityLevel?: SeniorityLevel;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  detail?: string; // Additional context for the badge
}

const getBadgeConfig = (colors: ReturnType<typeof useThemeColors>): Record<
  BadgeType,
  {
    label: string;
    icon: typeof Award;
    bgColor: string;
    textColor: string;
    iconColor: string;
  }
> => ({
  'serial-founder': {
    label: 'Serial Founder',
    icon: Star,
    bgColor: colors.amber.light,
    textColor: colors.amber.main,
    iconColor: colors.amber.main,
  },
  'linkedin-verified': {
    label: 'LinkedIn Verified',
    icon: Linkedin,
    bgColor: '#E7F3FF',
    textColor: '#0A66C2',
    iconColor: '#0A66C2',
  },
  'investor-seniority': {
    label: 'Investor',
    icon: Shield,
    bgColor: colors.primary[50],
    textColor: colors.primary[700],
    iconColor: colors.primary[600],
  },
  'actively-raising': {
    label: 'Actively Raising',
    icon: TrendingUp,
    bgColor: colors.traffic.greenLight,
    textColor: colors.traffic.green,
    iconColor: colors.traffic.green,
  },
  'verified-investment': {
    label: 'Verified',
    icon: CheckCircle,
    bgColor: colors.traffic.greenLight,
    textColor: colors.traffic.green,
    iconColor: colors.traffic.green,
  },
  // New investor badges
  'serial-investor': {
    label: 'Serial Investor',
    icon: Trophy,
    bgColor: colors.amber.light,
    textColor: colors.amber.main,
    iconColor: colors.amber.main,
  },
  'lead-investor': {
    label: 'Lead Investor',
    icon: Target,
    bgColor: colors.primary[50],
    textColor: colors.primary[700],
    iconColor: colors.primary[600],
  },
  'quick-responder': {
    label: 'Quick Responder',
    icon: Zap,
    bgColor: colors.traffic.greenLight,
    textColor: colors.traffic.green,
    iconColor: colors.traffic.green,
  },
  'first-check': {
    label: 'First Check',
    icon: Star,
    bgColor: '#F0E6FF',
    textColor: '#7C3AED',
    iconColor: '#7C3AED',
  },
  'follow-on-champion': {
    label: 'Follow-On Champion',
    icon: TrendingUp,
    bgColor: '#E0F7FA',
    textColor: '#00838F',
    iconColor: '#00838F',
  },
  'founder-favorite': {
    label: 'Founder Favorite',
    icon: Heart,
    bgColor: '#FCE4EC',
    textColor: '#C2185B',
    iconColor: '#C2185B',
  },
  'board-expert': {
    label: 'Board Expert',
    icon: Users,
    bgColor: '#E3F2FD',
    textColor: '#1565C0',
    iconColor: '#1565C0',
  },
  'sector-specialist': {
    label: 'Sector Specialist',
    icon: Briefcase,
    bgColor: '#FFF3E0',
    textColor: '#E65100',
    iconColor: '#E65100',
  },
});

const SENIORITY_LABELS: Record<SeniorityLevel, string> = {
  emerging: 'Emerging Investor',
  experienced: 'Experienced Investor',
  veteran: 'Veteran Investor',
};

const SIZE_CONFIG = {
  sm: {
    padding: 'px-2 py-1',
    iconSize: 12,
    textSize: 'text-xs',
    gap: 'gap-1',
  },
  md: {
    padding: 'px-3 py-1.5',
    iconSize: 14,
    textSize: 'text-xs',
    gap: 'gap-1.5',
  },
  lg: {
    padding: 'px-4 py-2',
    iconSize: 16,
    textSize: 'text-sm',
    gap: 'gap-2',
  },
};

export default function Badge({
  type,
  seniorityLevel,
  size = 'md',
  showLabel = true,
  detail,
}: BadgeProps) {
  const colors = useThemeColors();
  const BADGE_CONFIG = getBadgeConfig(colors);
  const config = BADGE_CONFIG[type as BadgeType];
  const sizeConfig = SIZE_CONFIG[size];

  if (!config) {
    return null;
  }

  // Override label for investor seniority
  let label = config.label;
  if (type === 'investor-seniority' && seniorityLevel) {
    label = SENIORITY_LABELS[seniorityLevel];
  } else if (type === 'sector-specialist' && detail) {
    label = detail; // Use sector name as label
  }

  const IconComponent = config.icon;

  return (
    <View
      className={`flex-row items-center ${sizeConfig.padding} ${sizeConfig.gap} rounded-full`}
      style={{ backgroundColor: config.bgColor }}
    >
      <IconComponent size={sizeConfig.iconSize} color={config.iconColor} />
      {showLabel && (
        <Text
          style={{ color: config.textColor }}
          className={`${sizeConfig.textSize} font-semibold`}
        >
          {label}
        </Text>
      )}
    </View>
  );
}

/**
 * Check if a founder qualifies as a serial founder
 * (has previous ventures listed)
 */
export function isSerialFounder(previousVentures?: string[]): boolean {
  return (previousVentures?.length ?? 0) > 0;
}

/**
 * Calculate investor seniority level based on investment history
 */
export function calculateSeniorityLevel(
  yearsInvesting?: number,
  investmentCount?: number
): SeniorityLevel {
  if (yearsInvesting && yearsInvesting >= 10) return 'veteran';
  if (yearsInvesting && yearsInvesting >= 5) return 'experienced';
  if (investmentCount && investmentCount >= 10) return 'experienced';
  if (investmentCount && investmentCount >= 20) return 'veteran';
  return 'emerging';
}

/**
 * Get badge display configuration for investor badge type
 */
export function getInvestorBadgeInfo(badgeType: InvestorBadgeType, detail?: string) {
  const badgeInfo: Record<InvestorBadgeType, { label: string; shortLabel: string }> = {
    'linkedin-verified': { label: 'LinkedIn Verified', shortLabel: 'Verified' },
    'serial-investor': { label: 'Serial Investor', shortLabel: '50+ deals' },
    'lead-investor': { label: 'Lead Investor', shortLabel: 'Leads rounds' },
    'quick-responder': { label: 'Quick Responder', shortLabel: '<48h' },
    'first-check': { label: 'First Check Investor', shortLabel: 'First check' },
    'follow-on-champion': { label: 'Follow-On Champion', shortLabel: 'Follows on' },
    'founder-favorite': { label: 'Founder Favorite', shortLabel: '4.5+ rating' },
    'board-expert': { label: 'Board Expert', shortLabel: detail || 'Board seats' },
    'sector-specialist': { label: detail || 'Sector Specialist', shortLabel: detail || 'Specialist' },
    'reliable-committer': { label: 'Reliable Committer', shortLabel: '80%+ follow-through' },
  };
  return badgeInfo[badgeType] || { label: badgeType, shortLabel: badgeType };
}
